import type { SentimentResult } from '../../types'

interface Props {
  data:    SentimentResult | null
  loading: boolean
}

const LABEL_CONFIG = {
  positive: { text: '強気',   bg: 'bg-trading-green/20', border: 'border-trading-green', text_color: 'text-trading-green' },
  negative: { text: '弱気',   bg: 'bg-trading-red/20',   border: 'border-trading-red',   text_color: 'text-trading-red'   },
  neutral:  { text: '中立',   bg: 'bg-trading-muted/20', border: 'border-trading-muted', text_color: 'text-trading-muted' },
} as const

export default function SentimentPanel({ data, loading }: Props) {
  if (loading) {
    return (
      <div className="rounded bg-trading-panel border border-trading-border p-3 animate-pulse">
        <div className="h-4 w-24 bg-trading-border rounded mb-3" />
        <div className="h-2 w-full bg-trading-border rounded" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="rounded bg-trading-panel border border-trading-border p-3 text-xs text-trading-muted text-center">
        センチメントデータなし
      </div>
    )
  }

  const cfg      = LABEL_CONFIG[data.label]
  const scoreAbs = Math.abs(data.score)
  const barColor = data.label === 'positive'
    ? 'bg-trading-green'
    : data.label === 'negative'
      ? 'bg-trading-red'
      : 'bg-trading-muted'

  const updatedMins = Math.round((Date.now() / 1000 - data.updated_at) / 60)

  return (
    <div className={`rounded border p-3 ${cfg.bg} ${cfg.border}`}>
      {/* ヘッダー行 */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.text_color} border ${cfg.border}`}>
            {cfg.text}
          </span>
          <span className={`text-sm font-bold ${cfg.text_color}`}>
            {data.score >= 0 ? '+' : ''}{(data.score * 100).toFixed(1)}
          </span>
        </div>
        <div className="text-right">
          <div className="text-[9px] text-trading-muted">{data.article_count} 件の記事</div>
          <div className="text-[9px] text-trading-muted">{updatedMins}分前</div>
        </div>
      </div>

      {/* センチメントゲージ */}
      <div className="mb-3">
        <div className="flex justify-between text-[8px] text-trading-muted mb-0.5">
          <span>弱気 −100</span>
          <span>中立 0</span>
          <span>+100 強気</span>
        </div>
        {/* 背景バー */}
        <div className="relative h-1.5 rounded-full bg-trading-border overflow-hidden">
          {/* 中央から方向にのびるバー */}
          <div
            className={`absolute top-0 h-full rounded-full ${barColor} transition-all duration-500`}
            style={{
              left:  data.score >= 0 ? '50%'    : `${50 - scoreAbs * 50}%`,
              width: `${scoreAbs * 50}%`,
            }}
          />
          {/* 中央マーカー */}
          <div className="absolute top-0 left-1/2 h-full w-px bg-trading-muted/60" />
        </div>
      </div>

      {/* 記事リスト */}
      {data.articles.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {data.articles.slice(0, 3).map((article, i) => {
            const artCfg = LABEL_CONFIG[article.label]
            return (
              <li key={i} className="flex gap-2 items-start">
                <span className={`mt-0.5 shrink-0 text-[8px] font-bold px-1 rounded ${artCfg.bg} ${artCfg.text_color}`}>
                  {article.score >= 0 ? '+' : ''}{(article.score * 100).toFixed(0)}
                </span>
                <a
                  href={article.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-trading-text hover:text-trading-blue leading-tight line-clamp-2"
                >
                  {article.title}
                </a>
              </li>
            )
          })}
        </ul>
      )}

      {data.engine === 'unavailable' && (
        <p className="mt-2 text-[9px] text-trading-muted">
          ※ FinBERT 未ロード — センチメントスコアは利用不可
        </p>
      )}
    </div>
  )
}
